/**
 * CreateEventPage ✦ iter194 — The Commissioning Surface
 * ─────────────────────────────────────────────────────────────
 * One authored choice → name + date → quiet confirmation → Stripe.
 *
 * The "Boost your night" add-on funnel and the Confetti/Disco/Sparkle
 * effects screen are RETIRED. Editions are emotionally complete on
 * purchase — companions live POST-purchase, inside the room.
 *
 * Constitutional citations (per /app/memory/EVENTDROP_BELIEF_SYSTEM.md):
 *   • Belief 2 — participation is the substrate, not the feature.
 *     The funnel no longer assembles features; it opens a room.
 *   • Belief 4 — the evening becomes a system. The funnel BECOMES
 *     part of the system the user just commissioned, instead of
 *     pretending to be SaaS configurator software.
 *   • Belief 6 — luxury implies depth; cheap explains everything.
 *     One quiet preview, one price, no checklist of inclusions.
 *   • Negative space — NOT gamified participation · NOT feature
 *     ladders · NOT a configurable product catalogue · NOT a
 *     shopping cart.
 *
 * Hard rules (iter194):
 *   • NO "£0 total" anywhere. The edition is selected; the price
 *     IS the price.
 *   • NO "Your event is ready" — that's legacy software vocabulary.
 *     Use "Your room is ready ✦".
 *   • NO add-on selection. NO effects. NO companions in the funnel.
 *   • If someone arrives at /create without an edition, render a
 *     calm bounce surface ("Choose an evening to begin ✦") rather
 *     than letting /create become a fallback configurator.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EventDropLogo } from '../components/EventDropLogo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Constitutional tokens (mirror EventLanding.js + SchoolsPage.js) ──
const SERIF       = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY       = '#FBEED0';
const IVORY_DIM   = 'rgba(251,238,208,0.72)';
const IVORY_GHOST = 'rgba(251,238,208,0.48)';
const GOLD        = '#D4AF5E';
const GOLD_MUTED  = 'rgba(212,170,82,0.78)';
const GOLD_FAINT  = 'rgba(212,170,82,0.18)';
const WALNUT      = '#0E0805';

// ── Editions (mirrors EDITIONS_CATALOGUE in backend/server.py) ──
// Single source of truth for editorial editions in the funnel.
// iter215 pricing refresh — Keepsake £30 / Room £60 / Archive £99 to
// match EDITIONS_CATALOGUE in backend/server.py. Leavers Night stays
// at £99 per the iter193/iter194 brief. NEVER edit a price here in
// isolation — it must move with the backend catalogue.
const EDITIONS = {
  keepsake: {
    name: 'The Keepsake',
    price: 30,
    positioning: 'A quiet shared memory space.',
    audience: 'wedding',
  },
  room: {
    name: 'The Room',
    price: 60,
    positioning: 'A live participation experience for the entire room.',
    audience: 'wedding',
  },
  archive: {
    name: 'The Archive',
    price: 99,
    positioning: 'A cinematic preservation system for the night.',
    audience: 'wedding',
  },
  leavers_night: {
    name: 'Leavers Night',
    price: 99,
    positioning: 'A live participation experience for the year group.',
    audience: 'schools',
  },
};

const resolveEditionKey = (raw) => {
  if (!raw) return null;
  const k = raw.toLowerCase().trim().replace(/-/g, '_');
  return EDITIONS[k] ? k : null;
};

// =========================================================================
// PAGE
// =========================================================================
const CreateEventPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editionKey = useMemo(() => resolveEditionKey(searchParams.get('edition')), [searchParams]);

  // If no edition was carried in, render the calm bounce surface.
  if (!editionKey) {
    return <ChooseAnEvening />;
  }

  return <CommissionFlow editionKey={editionKey} navigate={navigate} />;
};

// =========================================================================
// THE BOUNCE — "Choose an evening to begin ✦"
// =========================================================================
const ChooseAnEvening = () => (
  <div
    data-testid="create-bounce-surface"
    style={{
      minHeight: '100vh', background: WALNUT, color: IVORY,
      fontFamily: SERIF, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 24px', textAlign: 'center',
    }}
  >
    <div style={{ maxWidth: 520 }}>
      <p style={{
        margin: 0, marginBottom: 28,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }} data-testid="create-bounce-eyebrow">
        EventDrop ✦
      </p>
      <p style={{
        margin: 0,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(22px, 3vw, 30px)', lineHeight: 1.45,
        color: IVORY,
      }} data-testid="create-bounce-copy">
        Choose an evening to begin <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
      </p>
      <div style={{ marginTop: 40 }}>
        <Link
          to="/"
          data-testid="create-bounce-link"
          style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 15, color: IVORY_DIM,
            textDecoration: 'underline',
            textDecorationColor: GOLD_FAINT,
            textUnderlineOffset: 5,
          }}
        >
          See the editions
        </Link>
      </div>
    </div>
  </div>
);

// =========================================================================
// THE COMMISSIONING FLOW — two steps, single edition, no add-ons.
// =========================================================================
const CommissionFlow = ({ editionKey, navigate }) => {
  const edition = EDITIONS[editionKey];
  const [step, setStep] = useState(1);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [draftEventId, setDraftEventId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const placeholderName = edition.audience === 'schools'
    ? 'e.g. St Mary’s Primary · Year 6 Leavers'
    : 'e.g. The Smith Wedding';
  const nameLabel = edition.audience === 'schools' ? 'School & year group' : 'Event name';

  const onContinue = async (promoCodeFromStep2 = null) => {
    setError('');
    if (step === 1) {
      if (!eventName.trim()) {
        setError('A name for the evening, please.');
        return;
      }
      // Create the draft event before showing confirmation, so the
      // event_id is ready to hand to checkout.
      // iter216 — wrap the draft-create in a tiny retry-with-backoff
      // so a single Cloudflare blip or mobile-network hiccup doesn't
      // bounce the host out of the funnel with a "Couldn't open the
      // room" error after typing their event name. We only retry on
      // transient failures (network error or 5xx); 4xx errors
      // (validation) fail immediately.
      if (!draftEventId) {
        setSubmitting(true);
        const payload = {
          event_name: eventName.trim(),
          event_date: eventDate || undefined,
        };
        let lastErr = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const res = await axios.post(`${API}/events/draft`, payload);
            setDraftEventId(res.data.event_id);
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            const status = e?.response?.status;
            // Definitive 4xx — surface the backend's reason and stop.
            if (status && status >= 400 && status < 500) {
              break;
            }
            // Transient (no response / 5xx) — wait then retry.
            if (attempt < 2) {
              // 600ms, then 1.6s — keeps the total worst-case under
              // 2.5s, which is still "the night is settling in" tempo.
              await new Promise((r) => setTimeout(r, 600 * (attempt + 1) ** 2));
            }
          }
        }
        if (lastErr) {
          const status = lastErr?.response?.status;
          const detail = lastErr?.response?.data?.detail;
          if (detail) {
            setError(detail);
          } else if (status && status >= 500) {
            setError('The room is a bit slow to open ✦ Try once more in a moment.');
          } else {
            setError('Couldn’t open the room. Try again in a moment.');
          }
          setSubmitting(false);
          return;
        }
        setSubmitting(false);
      }
      setStep(2);
      return;
    }
    // Step 2 → hand off to /checkout with the edition as the package.
    // If a promo was applied here, carry it through so /checkout can
    // pre-apply it (no re-typing on the final review).
    const params = new URLSearchParams();
    params.set('package', editionKey);
    params.set('event_name', eventName);
    if (eventDate) params.set('event_date', eventDate);
    if (draftEventId) params.set('event_id', draftEventId);
    if (promoCodeFromStep2) params.set('promo', promoCodeFromStep2);
    navigate(`/checkout?${params.toString()}`);
  };

  return (
    <div
      data-testid="create-commission-flow"
      data-edition={editionKey}
      style={{
        minHeight: '100vh', background: WALNUT, color: IVORY,
        fontFamily: SERIF, overflowX: 'hidden',
      }}
    >
      <FlowNav editionName={edition.name} />
      <div style={{
        maxWidth: 640, margin: '0 auto',
        padding: 'clamp(48px, 8vh, 96px) 24px clamp(80px, 11vh, 128px)',
      }}>
        <StepIndicator step={step} />

        {step === 1 && (
          <Step1Details
            edition={edition}
            eventName={eventName}
            setEventName={setEventName}
            eventDate={eventDate}
            setEventDate={setEventDate}
            placeholderName={placeholderName}
            nameLabel={nameLabel}
            onContinue={onContinue}
            submitting={submitting}
            error={error}
          />
        )}
        {step === 2 && (
          <Step2Confirm
            edition={edition}
            editionKey={editionKey}
            eventName={eventName}
            eventDate={eventDate}
            onBack={() => setStep(1)}
            onContinue={onContinue}
            error={error}
          />
        )}
      </div>
      <GlobalStyles />
    </div>
  );
};

// =========================================================================
// FLOW NAV — quiet header that names the edition being commissioned.
// =========================================================================
const FlowNav = ({ editionName }) => (
  <header
    data-testid="flow-nav"
    style={{
      padding: '20px clamp(14px, 4vw, 24px)',
      borderBottom: `1px solid ${GOLD_FAINT}`,
    }}
  >
    <div style={{
      maxWidth: 1180, margin: '0 auto',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 22,
    }}>
      <Link to="/" data-testid="flow-nav-home" style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        textDecoration: 'none', color: IVORY,
      }}>
        <EventDropLogo size={24} />
        <span style={{ fontFamily: SERIF, fontSize: 18, letterSpacing: '-0.005em', fontWeight: 500 }}>
          EventDrop <span style={{ color: GOLD, fontSize: 13, marginLeft: 2 }}>✦</span>
        </span>
      </Link>
      <span data-testid="flow-nav-edition" style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }}>
        Commissioning {editionName} <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
      </span>
    </div>
  </header>
);

// =========================================================================
// STEP INDICATOR — two dots. No numbers. No labels.
// =========================================================================
const StepIndicator = ({ step }) => (
  <div data-testid="step-indicator" style={{
    display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 48,
  }}>
    {[1, 2].map((n) => (
      <span
        key={n}
        data-testid={`step-dot-${n}`}
        data-active={step >= n ? '1' : '0'}
        style={{
          width: 6, height: 6, borderRadius: 999,
          background: step >= n ? GOLD : 'rgba(212,170,82,0.20)',
          transition: 'background 360ms ease',
        }}
      />
    ))}
  </div>
);

// =========================================================================
// STEP 1 — Name + date
// =========================================================================
const Step1Details = ({ edition, eventName, setEventName, eventDate, setEventDate,
                       placeholderName, nameLabel, onContinue, submitting, error }) => (
  <div data-testid="step-1-details" style={{ animation: 'editorialFade 0.6s ease-out' }}>
    <p style={{
      margin: 0, marginBottom: 16,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
      color: GOLD_MUTED, textAlign: 'center',
    }} data-testid="step-1-eyebrow">
      {edition.name} <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
    </p>
    <h1 style={{
      margin: 0, textAlign: 'center',
      fontFamily: SERIF, fontWeight: 500,
      fontSize: 'clamp(28px, 4.4vw, 40px)', lineHeight: 1.16, letterSpacing: '-0.01em',
      color: IVORY,
    }} data-testid="step-1-heading">
      Open the evening.
    </h1>
    <p style={{
      margin: '20px auto 48px', maxWidth: 460, textAlign: 'center',
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 'clamp(14px, 1.6vw, 16px)', lineHeight: 1.65,
      color: IVORY_DIM,
    }}>
      A name, a date. Nothing else for now.
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Field label={nameLabel} testId="field-name">
        <input
          data-testid="input-event-name"
          type="text"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          placeholder={placeholderName}
          style={inputStyle}
        />
      </Field>
      <Field label="Date" testId="field-date" optional>
        <input
          data-testid="input-event-date"
          type="date"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
          style={inputStyle}
        />
      </Field>
    </div>

    {error && (
      <p data-testid="step-1-error" style={{
        margin: '24px 0 0',
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
        color: '#E4A87B', textAlign: 'center',
      }}>{error}</p>
    )}

    <div style={{ marginTop: 56, display: 'flex', justifyContent: 'center' }}>
      <button
        type="button"
        onClick={onContinue}
        disabled={submitting}
        data-testid="step-1-continue"
        style={primaryCta(!submitting)}
      >
        {submitting ? 'Opening the room…' : (
          <>Continue <span style={{ color: GOLD, marginLeft: 4 }}>✦</span></>
        )}
      </button>
    </div>
  </div>
);

// =========================================================================
// STEP 2 — Calm confirmation. The edition. The night. The price.
// =========================================================================
const Step2Confirm = ({ edition, editionKey, eventName, eventDate, onBack, onContinue, error }) => {
  const dateLabel = eventDate ? formatDate(eventDate) : null;
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoErr, setPromoErr] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);

  const finalPrice = promoApplied
    ? Math.max(0, Math.round(edition.price * (1 - promoDiscount / 100) * 100) / 100)
    : edition.price;

  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoErr('');
    setPromoChecking(true);
    try {
      const res = await fetch(`${API}/promo/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      // Parse body in its own try so a malformed JSON body on a network
      // error doesn't drop us into the outer catch with a misleading
      // "couldn't reach the promo service" message.
      let data = null;
      try { data = await res.json(); } catch { /* keep data = null */ }
      if (res.ok && data && data.valid) {
        setPromoDiscount(data.discount || 0);
        setPromoApplied(true);
        setPromoCode(code); // normalised
      } else {
        setPromoErr((data && data.detail) || 'Code not recognised.');
        setPromoApplied(false);
        setPromoDiscount(0);
      }
    } catch {
      setPromoErr('Couldn’t reach the promo service. Try again in a moment.');
    } finally {
      setPromoChecking(false);
    }
  };
  const removePromo = () => {
    setPromoApplied(false);
    setPromoDiscount(0);
    setPromoCode('');
    setPromoErr('');
  };

  const continueWithPromo = () => onContinue(promoApplied ? promoCode : null);

  return (
    <div data-testid="step-2-confirm" style={{ animation: 'editorialFade 0.6s ease-out' }}>
      <p style={{
        margin: 0, marginBottom: 16,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: GOLD_MUTED, textAlign: 'center',
      }} data-testid="step-2-eyebrow">
        Ready when you are
      </p>
      <h1 style={{
        margin: 0, textAlign: 'center',
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(28px, 4.4vw, 40px)', lineHeight: 1.12, letterSpacing: '-0.01em',
        color: IVORY,
      }} data-testid="step-2-heading">
        Your room is ready <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
      </h1>

      <article data-testid="confirm-card" style={{
        marginTop: 56,
        padding: 'clamp(32px, 4vw, 44px)',
        background: 'linear-gradient(180deg, rgba(28,18,10,0.65) 0%, rgba(14,8,5,0.78) 100%)',
        border: `1px solid ${GOLD_MUTED}`,
        borderRadius: 18,
        boxShadow: '0 30px 90px rgba(0,0,0,0.5), 0 0 80px rgba(212,170,82,0.06) inset',
      }}>
        <p style={{
          margin: 0, marginBottom: 8,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: GOLD,
        }} data-testid="confirm-edition-eyebrow">
          {edition.name} <span style={{ fontStyle: 'normal' }}>✦</span>
        </p>
        <p style={{
          margin: 0,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, lineHeight: 1.55,
          color: IVORY_DIM,
        }} data-testid="confirm-positioning">
          {edition.positioning}
        </p>

        <div style={{ margin: '32px 0', height: 1, background: GOLD_FAINT }} />

        <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Row label="Name" value={eventName} testId="confirm-event-name" />
          {dateLabel && <Row label="Date" value={dateLabel} testId="confirm-event-date" />}
        </dl>

        <div style={{
          marginTop: 36, paddingTop: 28,
          borderTop: `1px solid ${GOLD_FAINT}`,
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12,
        }}>
          <span style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }} data-testid="confirm-price-label">
            {edition.name}
          </span>
          <span data-testid="confirm-price" style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 14,
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(28px, 3.8vw, 40px)', letterSpacing: '-0.01em',
            color: GOLD,
          }}>
            {promoApplied && (
              <span data-testid="confirm-price-original" style={{
                fontSize: 'clamp(18px, 2.4vw, 22px)',
                color: IVORY_GHOST,
                textDecoration: 'line-through',
                textDecorationColor: 'rgba(251,238,208,0.32)',
                fontWeight: 400,
              }}>
                £{edition.price}
              </span>
            )}
            <span data-testid="confirm-price-final">£{finalPrice}</span>
          </span>
        </div>
        {promoApplied && (
          <p data-testid="confirm-promo-tag" style={{
            margin: '12px 0 0',
            textAlign: 'right',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }}>
            {promoCode} <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>{' '}
            <button onClick={removePromo} data-testid="confirm-promo-remove" style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: IVORY_GHOST, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.06em',
              textDecoration: 'underline', textDecorationColor: GOLD_FAINT,
              marginLeft: 8,
            }}>remove</button>
          </p>
        )}

        {/* ── Quiet promo affordance ──────────────────────────
            Closed by default. Belief 6: luxury implies depth.
            Discovery happens at the editorial moment, not at the cashier. */}
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          {!promoOpen && !promoApplied && (
            <button
              type="button"
              onClick={() => setPromoOpen(true)}
              data-testid="confirm-promo-toggle"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13, color: IVORY_GHOST,
                textDecoration: 'underline',
                textDecorationColor: GOLD_FAINT,
                textUnderlineOffset: 4, letterSpacing: '0.04em',
              }}
            >
              Have a promo code? <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
            </button>
          )}
          {promoOpen && !promoApplied && (
            <div data-testid="confirm-promo-input-row" style={{
              display: 'flex', gap: 10, marginTop: 6,
              maxWidth: 380, marginLeft: 'auto', marginRight: 'auto',
            }}>
              <input
                data-testid="confirm-promo-input"
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value); setPromoErr(''); }}
                placeholder="Enter your code"
                onKeyDown={(e) => { if (e.key === 'Enter') applyPromo(); }}
                style={{
                  flex: 1, padding: '12px 14px',
                  background: 'rgba(28,18,10,0.6)',
                  border: `1px solid ${GOLD_FAINT}`,
                  borderRadius: 10, color: IVORY,
                  fontFamily: SERIF, fontSize: 14, letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  outline: 'none',
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={applyPromo}
                disabled={promoChecking || !promoCode.trim()}
                data-testid="confirm-promo-apply"
                style={{
                  padding: '12px 18px', borderRadius: 999,
                  background: 'rgba(212,170,82,0.10)',
                  border: `1px solid ${promoChecking || !promoCode.trim() ? GOLD_FAINT : GOLD_MUTED}`,
                  color: IVORY,
                  fontFamily: SERIF, fontStyle: 'italic', fontSize: 13,
                  cursor: promoChecking || !promoCode.trim() ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {promoChecking ? '…' : 'Apply'}
              </button>
            </div>
          )}
          {promoErr && (
            <p data-testid="confirm-promo-error" style={{
              margin: '10px 0 0',
              fontFamily: SERIF, fontStyle: 'italic', fontSize: 12,
              color: '#E4A87B',
            }}>{promoErr}</p>
          )}
        </div>
      </article>

      {error && (
        <p data-testid="step-2-error" style={{
          margin: '24px 0 0',
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
          color: '#E4A87B', textAlign: 'center',
        }}>{error}</p>
      )}

      <div style={{ marginTop: 48, display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={continueWithPromo}
          data-testid="step-2-continue"
          style={primaryCta(true)}
        >
          Continue to checkout <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
        </button>
      </div>
      <p style={{
        margin: '20px auto 0', maxWidth: 360, textAlign: 'center',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, color: IVORY_GHOST, letterSpacing: '0.04em',
      }} data-testid="confirm-stripe-note">
        Secure payment via Stripe. No subscription.
      </p>
      <div style={{ marginTop: 28, textAlign: 'center' }}>
        <button
          type="button"
          onClick={onBack}
          data-testid="step-2-back"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, color: IVORY_GHOST,
            textDecoration: 'underline',
            textDecorationColor: GOLD_FAINT,
            textUnderlineOffset: 4,
          }}
        >
          Edit details
        </button>
      </div>
    </div>
  );
};

// ── small primitives ─────────────────────────────────────────────
const Field = ({ label, optional, testId, children }) => (
  <div data-testid={testId}>
    <label style={{
      display: 'block', marginBottom: 12,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: GOLD_MUTED,
    }}>
      {label}
      {optional && <span style={{
        marginLeft: 8, color: IVORY_GHOST, fontSize: 11, letterSpacing: '0.06em',
      }}>· optional</span>}
    </label>
    {children}
  </div>
);

const Row = ({ label, value, testId }) => (
  <div data-testid={testId} style={{
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: 16, flexWrap: 'wrap',
  }}>
    <dt style={{
      margin: 0,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: GOLD_MUTED,
    }}>{label}</dt>
    <dd style={{
      margin: 0,
      fontFamily: SERIF, fontSize: 16, color: IVORY,
      textAlign: 'right',
    }}>{value}</dd>
  </div>
);

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '16px 18px', borderRadius: 12,
  background: 'rgba(28,18,10,0.55)',
  border: `1px solid ${GOLD_FAINT}`,
  color: IVORY,
  fontFamily: SERIF, fontSize: 16,
  letterSpacing: '0.005em',
  outline: 'none',
  transition: 'border-color 280ms ease',
};

const primaryCta = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 10,
  padding: '16px 32px', borderRadius: 999,
  background: active ? 'rgba(212,170,82,0.10)' : 'rgba(212,170,82,0.04)',
  border: `1px solid ${active ? GOLD : GOLD_FAINT}`,
  color: active ? IVORY : IVORY_GHOST,
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 16, letterSpacing: '0.01em',
  textDecoration: 'none',
  cursor: active ? 'pointer' : 'not-allowed',
  transition: 'border-color 360ms ease, color 360ms ease',
});

// ── small helpers ────────────────────────────────────────────────
const formatDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

// ── global styles ─────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    [data-testid="create-commission-flow"] ::selection {
      background: rgba(212,170,82,0.22);
      color: #FFF5DA;
    }
    [data-testid="create-commission-flow"] input::placeholder {
      color: rgba(251,238,208,0.32);
      font-style: italic;
    }
    [data-testid="create-commission-flow"] input:focus {
      border-color: ${GOLD_MUTED} !important;
    }
    [data-testid="create-commission-flow"] input::-webkit-calendar-picker-indicator {
      filter: invert(0.7) sepia(0.6) saturate(2) hue-rotate(2deg); cursor: pointer;
    }
    @keyframes editorialFade {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-testid="create-commission-flow"] * {
        animation-duration: 0.001ms !important;
        transition-duration: 0.001ms !important;
      }
    }
  `}</style>
);

export default CreateEventPage;
